import { useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';

export const useAuth = () => {
  const { keycloak } = useKeycloak();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to refresh token
  const refreshToken = async (): Promise<boolean> => {
    try {
      console.log('🔄 Attempting to refresh token...');
      const refreshed = await keycloak.updateToken(0);
      console.log('🔄 Token refresh result:', refreshed);
      
      if (refreshed || keycloak.token) {
        console.log('✅ Token refreshed successfully');
        return true;
      } else {
        console.error('❌ Token refresh failed');
        return false;
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      
      // Handle specific token refresh errors
      if (error instanceof Error) {
        console.error('❌ Token refresh error details:', error.message);
        
        // If token is completely invalid, redirect to login
        if (error.message.includes('Failed to refresh token') || 
            error.message.includes('HTTP error') ||
            error.message.includes('Network error')) {
          console.log('🔐 Token refresh failed with network error, redirecting to login...');
          try {
            await keycloak.logout();
          } catch (logoutError) {
            console.error('❌ Logout error:', logoutError);
          }
          return false;
        }
      }
      
      // Don't throw the error - just return false to indicate failure
      // This prevents it from becoming an unhandled rejection
      return false;
    }
  };

  // Function to handle authentication errors
  const handleAuthError = async (error: any): Promise<boolean> => {
    if (error?.message?.includes('401') || error?.message?.includes('Authentication failed')) {
      console.log('🔐 Authentication error detected, attempting token refresh...');
      
      const refreshed = await refreshToken();
      if (refreshed) {
        console.log('✅ Token refreshed, retry the operation');
        return true; // Indicates retry should be attempted
      } else {
        console.error('❌ Token refresh failed, redirecting to login');
        keycloak.login();
        return false; // Indicates no retry should be attempted
      }
    }
    return false; // Not an auth error
  };

  // Setup automatic token refresh
  useEffect(() => {
    const setupTokenRefresh = () => {
      // Clear existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      if (keycloak.token) {
        // Get token expiration time
        const tokenParsed = keycloak.tokenParsed as any;
        if (tokenParsed?.exp) {
          const expiresAt = tokenParsed.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          const timeUntilExpiry = expiresAt - now;
          
          // Refresh token 5 minutes before expiry
          const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 60000); // At least 1 minute
          
          console.log('⏰ Token expires in:', Math.round(timeUntilExpiry / 1000), 'seconds');
          console.log('🔄 Will refresh token in:', Math.round(refreshTime / 1000), 'seconds');
          
          refreshTimeoutRef.current = setTimeout(async () => {
            console.log('⏰ Token refresh timer triggered');
            try {
              const success = await refreshToken();
              if (success) {
                setupTokenRefresh(); // Setup next refresh only if successful
              } else {
                console.log('⏰ Token refresh failed, stopping automatic refresh');
              }
            } catch (error) {
              console.log('⏰ Token refresh error caught, stopping automatic refresh');
              // Don't re-throw - this prevents unhandled rejections
            }
          }, refreshTime);
        }
      }
    };

    setupTokenRefresh();

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [keycloak.token, keycloak.tokenParsed]);

  return {
    token: keycloak.token,
    isAuthenticated: !!keycloak.token,
    refreshToken,
    handleAuthError,
    login: keycloak.login,
    logout: keycloak.logout
  };
}; 